import { useCallback, useEffect, useState } from "react";

import {
  CATEGORIES,
  CHECKLIST,
  CHECKLIST_BY_CATEGORY,
  CHECKLIST_IDS,
  categoryLabel,
  type Category,
  type ChecklistItem,
} from "@nav-games/predeparture";

interface PreDepartureProps {
  onNavigate: (to: string) => void;
}

const STORAGE_KEY = "predeparture.v1";

interface PersistState {
  /// Item id → checked at timestamp (ms). Absence means unchecked.
  ticks: Record<string, number>;
  /// Last reset wall-clock time, for "last reset" display.
  resetAt: number | null;
}

const EMPTY_STATE: PersistState = { ticks: {}, resetAt: null };

function readState(): PersistState {
  if (typeof window === "undefined") return EMPTY_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw) as Partial<PersistState>;
    return {
      ticks:
        parsed.ticks && typeof parsed.ticks === "object" ? parsed.ticks : {},
      resetAt: typeof parsed.resetAt === "number" ? parsed.resetAt : null,
    };
  } catch {
    return EMPTY_STATE;
  }
}

function writeState(state: PersistState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage full or disabled — fall back to in-memory only */
  }
}

/// Interactive pre-departure checklist. Tap each item to mark it
/// done; state persists in localStorage so the same browser/device
/// resumes where it left off. The checklist is intentionally not
/// per-user — many sailors share a phone or use a shared crew
/// device for the run-through.
export function PreDeparture({ onNavigate }: PreDepartureProps) {
  const [state, setState] = useState<PersistState>(() => readState());

  useEffect(() => {
    writeState(state);
  }, [state]);

  const toggle = useCallback((id: string) => {
    setState((cur) => {
      const next = { ...cur.ticks };
      if (id in next) {
        delete next[id];
      } else {
        next[id] = Date.now();
      }
      return { ...cur, ticks: next };
    });
  }, []);

  const resetAll = useCallback(() => {
    if (typeof window !== "undefined") {
      if (!window.confirm("Clear every check? This can't be undone.")) return;
    }
    setState({ ticks: {}, resetAt: Date.now() });
  }, []);

  const tickAll = useCallback(() => {
    const now = Date.now();
    const ticks: Record<string, number> = {};
    for (const id of CHECKLIST_IDS) ticks[id] = now;
    setState((cur) => ({ ...cur, ticks }));
  }, []);

  const checkedCount = Object.keys(state.ticks).length;
  const totalCount = CHECKLIST.length;
  const pct = Math.round((checkedCount / totalCount) * 100);

  return (
    <>
      <h2 style={{ marginTop: 0 }}>Pre-departure check</h2>
      <p className="muted">
        The list every skipper runs before slipping lines. Tap each
        item to tick it off — your progress is saved to this browser
        and persists across sessions. Reset before each new passage.
      </p>

      <div
        className="card"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: "var(--bg-card)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: 8,
          }}
        >
          <strong style={{ fontSize: 18 }}>
            {checkedCount} / {totalCount}
          </strong>
          <span
            className="small muted"
            style={{
              color:
                pct === 100
                  ? "var(--success)"
                  : pct >= 75
                    ? "var(--warn)"
                    : "var(--text-muted)",
              fontWeight: 600,
            }}
          >
            {pct}% complete
          </span>
          {state.resetAt && (
            <span className="small muted">
              Last reset {formatRelativeTime(state.resetAt)}
            </span>
          )}
        </div>
        <ProgressBar pct={pct} />
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            className="secondary"
            onClick={tickAll}
            disabled={checkedCount === totalCount}
            style={{ fontSize: 13, padding: "6px 12px" }}
          >
            Tick all
          </button>
          <button
            className="secondary"
            onClick={resetAll}
            disabled={checkedCount === 0}
            style={{ fontSize: 13, padding: "6px 12px" }}
          >
            Reset all
          </button>
        </div>
      </div>

      {CATEGORIES.map((c) => (
        <CategorySection
          key={c}
          category={c}
          items={CHECKLIST_BY_CATEGORY[c] ?? []}
          ticks={state.ticks}
          onToggle={toggle}
        />
      ))}

      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>WOBBLE — pre-engine checks drill</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Quick-fire drill on the RYA pre-engine mnemonic — Water, Oil,
          Belts, Bilges, Leaks, Exhaust. Both letter-to-check recall and
          "what would have caught this fault" scenarios.
        </p>
        <button
          onClick={() => onNavigate("/drills/predeparture/wobble")}
          className="secondary"
        >
          Open WOBBLE drill
        </button>
      </div>

      <button className="secondary" onClick={() => onNavigate("/drills")}>
        ← Back to Drills
      </button>
    </>
  );
}

function CategorySection({
  category,
  items,
  ticks,
  onToggle,
}: {
  category: Category;
  items: readonly ChecklistItem[];
  ticks: Record<string, number>;
  onToggle: (id: string) => void;
}) {
  const done = items.filter((i) => i.id in ticks).length;
  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <h3 style={{ margin: 0 }}>{categoryLabel(category)}</h3>
        <span
          className="small muted"
          style={{
            color: done === items.length ? "var(--success)" : "var(--text-muted)",
            fontWeight: 600,
          }}
        >
          {done} / {items.length}
        </span>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0" }}>
        {items.map((item) => (
          <ChecklistRow
            key={item.id}
            item={item}
            checked={item.id in ticks}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </div>
  );
}

function ChecklistRow({
  item,
  checked,
  onToggle,
}: {
  item: ChecklistItem;
  checked: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <li
      style={{
        borderTop: "1px solid var(--border)",
        padding: "10px 0",
      }}
    >
      <label
        style={{
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(item.id)}
          style={{
            marginTop: 3,
            width: 20,
            height: 20,
            cursor: "pointer",
            flexShrink: 0,
          }}
          aria-describedby={item.note ? `${item.id}-note` : undefined}
        />
        <span style={{ flex: 1 }}>
          <span
            style={{
              fontWeight: 600,
              textDecoration: checked ? "line-through" : "none",
              color: checked ? "var(--text-muted)" : "var(--text)",
            }}
          >
            {item.text}
          </span>
          {item.note && (
            <span
              id={`${item.id}-note`}
              className="small muted"
              style={{ display: "block", marginTop: 4 }}
            >
              {item.note}
            </span>
          )}
        </span>
      </label>
    </li>
  );
}

function ProgressBar({ pct }: { pct: number }) {
  return (
    <div
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        width: "100%",
        height: 6,
        background: "var(--bg-elev)",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          background:
            pct === 100
              ? "var(--success)"
              : pct >= 75
                ? "var(--warn)"
                : "var(--accent)",
          transition: "width 0.2s ease-out",
        }}
      />
    </div>
  );
}

function formatRelativeTime(at: number): string {
  const delta = Date.now() - at;
  if (delta < 60_000) return "just now";
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)} min ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)} hr ago`;
  return new Date(at).toLocaleDateString();
}
